/**
 * @vortex-os/ontos — agent-mediated LLM enrichment (0.2.0).
 *
 * The pure, testable core: build an extraction request, validate the agent's raw
 * proposals (vocab + domain/range + id resolution), tier them (deterministic /
 * cross-model / self), and store them as PROPOSALS — never into the trusted
 * `relations` graph. The agent I/O (printing the request, reading JSON back) is a
 * thin CLI wrapper layered on top (§6, a later item).
 */

import { createHash } from "node:crypto";
import type { OntosStore } from "../sqlite/store.js";
import type { RecordInput } from "../extract/types.js";
import { validateProposal } from "./validate.js";
import { createLocalResolver } from "./resolve.js";
import type { Resolver } from "./validate.js";
import { tierProposal } from "./tier.js";
import type { IngestReport, RawProposal, RejectReason } from "./types.js";

export * from "./types.js";
export {
  OBJECT_TYPES,
  RELATION_TYPES,
  DOMAIN_RANGE,
  typeFromId,
  validateProposal,
  buildEnrichmentRequest,
  requestFor,
} from "./validate.js";
export type { Resolver } from "./validate.js";
export { createLocalResolver } from "./resolve.js";
export type { LocalResolver } from "./resolve.js";
export { tierProposal } from "./tier.js";
import { gatedVerifier } from "./sensitivity.js";
export {
  classifySensitivity,
  candidateSensitivity,
  crossVendorEgressEligible,
  egressPayload,
  gatedVerifier,
  QUOTE_EGRESS_CAP,
  REF_EGRESS_CAP,
} from "./sensitivity.js";
export type { SensitivityVerdict, EgressGateInput, EgressDecision, EgressPayload } from "./sensitivity.js";
export {
  isSafeModelId,
  parseEconomyModelPolicy,
  selectCodexEconomyModel,
  resolveEconomyModel,
} from "./model-policy.js";
export type {
  EconomyModelPolicy,
  CodexModelCatalogEntry,
  CodexModelCatalog,
} from "./model-policy.js";
export {
  deriveConversationUse,
  deriveGuardEligible,
  applyDecay,
  clampToParent,
  initialScore,
  evidenceKindFromVerifiedBy,
  sameUtcDay,
  SCORE,
  HIGH_AT,
  MEDIUM_AT,
  DECAY_DAYS,
} from "./trust-policy.js";
export {
  parseTranscript,
  chunkMessages,
  quoteHash,
  anchorValid,
  buildConversationRequest,
  ingestConversationProposals,
  CONVERSATION_RULES,
  slugify,
  validateObjectProposal,
  tierObjectProposal,
  ingestObjectProposals,
} from "./conversation.js";
export type {
  Speaker,
  TranscriptMessage,
  ParsedTranscript,
  Chunk,
  ConversationExtractionRequest,
  RawConversationProposal,
  ConversationIngestOptions,
  ObjectIngestReport,
} from "./conversation.js";
// Backlog counts + extraction-run marker (tiered-extraction status; cycle-safe:
// status.ts only calls back into hoisted functions of this module at call time).
export {
  enrichmentSources,
  listEnrichableRecords,
  recordDate,
  localToday,
  enrichmentStatus,
  conversationBacklog,
  extractionRunPath,
  writeExtractionRun,
  clearExtractionRun,
  readExtractionRun,
  EXTRACTION_RUN_STALE_MS,
} from "./status.js";
export type {
  EnrichableCategory,
  EnrichmentSource,
  CategoryBacklog,
  EnrichmentStatus,
  ConversationBacklog,
  ExtractionRun,
} from "./status.js";

/**
 * Extraction hash for change detection (§4 step 3): a record needs (re-)enrichment
 * only when its text changed. Hashes the body — frontmatter-only edits go through
 * deterministic extraction, not the LLM gap-filler.
 */
export function extractionHash(record: Pick<RecordInput, "body">): string {
  return createHash("sha256").update(record.body).digest("hex").slice(0, 16);
}

/**
 * True when this record's text changed since it was last enriched.
 *
 * KNOWN LIMITATION (first cut, by design — proposals live in a disposable index):
 * a RENAME/DELETE leaves the old path's `enrichment_state` and any proposals it
 * produced as orphans, and a label resolved only LATER (unresolved at ingest →
 * aliased afterwards) gets a fresh relId on re-ingest, so the old low-trust row is
 * not upgraded in place. Both are low-harm (disposable, weakly surfaced) and are
 * cleaned by a future reconcile/GC pass + `enrich --rebuild`; tracked as a 0.2.0
 * follow-up, not a blocker (Codex review 2026-06-18).
 */
export function needsEnrichment(store: OntosStore, record: RecordInput): boolean {
  return store.getExtractionHash(record.path) !== extractionHash(record);
}

export interface IngestOptions {
  /** Resolve a proposed ref to a canonical object id (default: the local
   *  extract-then-merge ladder — exact id/alias, then narrow slug equality). */
  readonly resolve?: Resolver;
  /** Stamp stored proposals with the record's extraction hash (change-detection link). */
  readonly sourceRecordHash?: string | null;
  /** ISO timestamp for proposedAt (pass in — the addon never reads the wall clock itself). */
  readonly proposedAt?: string | null;
  /** Phase-2: "conversation" caps trust at MEDIUM. Defaults to "record" (Phase-1). */
  readonly sourceKind?: import("./types.js").SourceKind;
  /** Phase-2: originating transcript/record id (circular-corroboration guard). */
  readonly sourceFamily?: string | null;
  /** Phase-2: batch id, for run-level rollback. */
  readonly extractionRunId?: string | null;
  /** Cross-vendor egress opt-in (the `--cross-vendor-egress` flag). Default false ⇒ no
   *  cross-model verdict is honored on the record path either (gate is ontos-wide). */
  readonly crossVendorEgress?: boolean;
}

/**
 * Ingest the agent's raw proposals for a record: validate → tier → store as
 * proposals. Invalid or verifier-disagreed proposals are dropped into the report,
 * never stored. The trusted graph and objects table are untouched.
 */
export function ingestProposals(
  store: OntosStore,
  raws: readonly RawProposal[],
  opts: IngestOptions = {},
): IngestReport {
  // Default = the local extract-then-merge ladder (exact id/alias, then narrow
  // slug equality) — see resolve.ts. Callers may still inject their own.
  const resolve: Resolver = opts.resolve ?? createLocalResolver(store).resolveRef;
  const hasDeterministic = (relId: string): boolean =>
    store.relationProvenance(relId) === "deterministic";
  const rejected: { reason: RejectReason; raw: RawProposal }[] = [];
  let accepted = 0;

  for (const raw of raws) {
    const v = validateProposal(raw, resolve);
    if (!v.ok) {
      rejected.push({ reason: v.reason, raw });
      continue;
    }
    // Egress gate (ontos-wide): a cross-model verdict is honored only with egress on +
    // affirmed non-sensitive + clean deterministic floor; else it tiers as self/LOW.
    const verifier = gatedVerifier(raw.verifier, raw.sensitive, opts.crossVendorEgress === true, {
      quote: raw.evidenceSpan,
      sourceRef: raw.sourceRef,
      rel: raw.rel,
      targetRef: raw.targetRef,
    });
    const t = tierProposal(v.value, { ...raw, verifier }, hasDeterministic, {
      sourceRecordHash: opts.sourceRecordHash,
      proposedAt: opts.proposedAt,
      sourceKind: opts.sourceKind,
      sourceFamily: opts.sourceFamily,
      extractionRunId: opts.extractionRunId,
    });
    if (!t.ok) {
      rejected.push({ reason: t.reason, raw });
      continue;
    }
    store.upsertProposal(t.value);
    accepted++;
  }

  return { accepted, rejected };
}
